// review-order-modal.component.ts
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpContext } from '@angular/common/http';
import { finalize } from 'rxjs';
import { SILENT_HTTP_ERRORS } from '../../commons/http-interceptor.service';
import { FunctionsService } from '../../data/functions.service';
import { Review } from '../models/review';

/**
 * Dados de abertura do modal. O nome já vem do caller (`compra.vendedor.nome`
 * / `venda.vendedor.nome`) — mostrado na hora, sem esperar rede. `vendorUid`
 * dispara a busca da ficha completa (foto/localização/nota) via `sellers/v1`;
 * sem ele (callers antigos que ainda abrem com `data: ''`), o modal segue
 * funcionando, só sem a ficha.
 */
export interface ReviewOrderModalData {
  vendorUid?: string | null;
  vendorName?: string | null;
}

/** Recorte de `SellerInfoResponse` que a ficha do vendedor exibe aqui. */
interface FichaVendedor {
  averageRating: number;
  totalReviews: number;
  location: string | null;
  profilePhoto: string | null;
}

@Component({
  selector: 'app-review-order-modal',
  templateUrl: './review-order-modal.component.html',
  styleUrls: ['./review-order-modal.component.css']
})
export class ReviewOrderModalComponent {
  reviews: string[] = [
    'review_1',
    'review_2',
    'review_3',
    'review_4',
    'review_5'
  ];
  reviewText: string;
  currentRatingDescription: string = '';
  @Input() rating: number;
  @Output() ratingChange: EventEmitter<number> = new EventEmitter();

  stars: boolean[] = Array(5).fill(false);

  /** Nome mostrado imediatamente (fallback do caller, não espera rede). */
  vendorName: string | null = null;
  /** Preenchida quando `sellers/v1` responde; `null` enquanto carrega ou se falhar. */
  ficha: FichaVendedor | null = null;
  fichaCarregando = false;

  constructor(
    public dialogRef: MatDialogRef<ReviewOrderModalComponent, Review>,
    @Inject(MAT_DIALOG_DATA) public data: ReviewOrderModalData,
    private functionsService: FunctionsService,
  ) {
    this.vendorName = data?.vendorName ?? null;
  }

  ngOnInit() {
    this.updateStars();
    this.carregarFicha();
  }

  /**
   * Busca a ficha do vendedor sob demanda, ao abrir. É só enriquecimento
   * visual do painel esquerdo — diferente do fluxo de "Avaliar produto", a
   * avaliação em si não depende dela. Por isso o `error` fica silencioso: sem
   * toast, sem bloquear o formulário, a ficha só permanece com o que já tinha
   * (o nome vindo do caller).
   */
  private carregarFicha(): void {
    const uid = this.data?.vendorUid;
    if (!uid) return;

    this.fichaCarregando = true;
    this.functionsService.sellerInfo(
      { uid },
      { context: new HttpContext().set(SILENT_HTTP_ERRORS, true) },
    ).pipe(
      finalize(() => this.fichaCarregando = false)
    ).subscribe({
      next: (res) => {
        const info = res.body?.data;
        if (!info) return;
        this.ficha = {
          averageRating: info.averageRating || 0,
          totalReviews: info.totalReviews || 0,
          location: info.location || null,
          profilePhoto: info.profilePhoto || null,
        };
      },
      error: () => {},
    });
  }

  /**
   * Estrelas de LEITURA da ficha (nota já existente do vendedor) — não
   * confundir com `stars`, que é a nota que o usuário está dando agora.
   */
  estrelasDaFicha(nota: number): boolean[] {
    const cheias = Math.round(nota);
    return Array.from({ length: 5 }, (_, i) => i < cheias);
  }

  updateStars() {
    this.stars = this.stars.map((_, i) => i < this.rating);
  }

  rate(rating: number) {
    this.rating = rating;
    this.currentRatingDescription = this.reviews[rating - 1];
    this.ratingChange.emit(this.rating);
    this.updateStars();
  }

  isSubmitDisabled(): boolean {
    return this.rating === undefined;
  }

  submitReview() {
    this.dialogRef.close({ rating: this.rating, review: this.reviewText });
  }
}
